import { BrowserRouter, Navigate, Route, Routes } from "react-router";

// Toolkit imports
import { ApiCacheProvider } from "sj-ui-toolkit/src/api/cache";
import { CookieConsent } from "sj-ui-toolkit/src/atlas/components";
import { LearnAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { useAnalytics } from "sj-ui-toolkit/src/hooks";
import { ErrorPage } from "sj-ui-toolkit/src/atlas/components/ErrorPage/ErrorPage";

// Context
import { LearnContextProvider } from "./context/LearnContext";

// Shared layout
import Layout from "./components/Layout/Layout";

// Route components
import Dashboard from "./components/Dashboard/Dashboard";
import LessonPage from "./components/Lesson/LessonPage";
import ExercisePage from "./components/Exercise/ExercisePage";
import SingleLessonGuard from "./components/Lesson/SingleLessonGuard";
import Path from "./components/Path/Path";
import TournamentPage from "./components/Tournament/TournamentPage";
import TournamentContentPage from "./components/Tournament/TournamentContentPage";
import Assessments from "./components/Assessments/Assessments";

// Guest access component
import GuestLessonAccess from "./components/Guest/GuestLessonAccess";
import Catalog from "./components/Catalog/Catalog";
import RequireCatalogEnabled from "./components/Catalog/RequireCatalogEnabled";
import LevelPage from "./components/Level/LevelPage";
import QuestsPage from "./components/Quests/QuestsPage";
import TestConnection from "./components/TestConnection/TestConnection";
import PersonalizedLessonAssignmentPage from "./components/PersonalizedLessonAssignment/PersonalizedLessonAssignmentPage";
import FeedbackPage from "./components/Feedback/FeedbackPage";
import LeaderboardPage from "./components/Leaderboard/LeaderboardPage";
import AssessmentRedirect from "./components/Redirects/AssessmentRedirect";
import LegacyLessonRedirect from "./components/Redirects/LegacyLessonRedirect";
import ProfileAchievementRedirect from "./components/Redirects/ProfileAchievementRedirect";
import ScormLoginRedirect from "./components/Redirects/ScormLoginRedirect";
import AspenChatPage from "./components/AspenChat/AspenChatPage";

// Temporary redirect constants
const Redirects = {
  journey: "journey",
  assignments: "account/assignments",
  tournamentsFinished: "/tournaments/finished",
  profileAchievements: "/account/achievements",
  signIn: "/user/sign_in",
  modulesCatalog: "/modules/catalog",
};

function App() {
  useAnalytics();

  return (
    <BrowserRouter>
      <CookieConsent />
      <ApiCacheProvider>
        <LearnContextProvider>
          <Routes>
            <Route path={LearnAppRoutes.route} element={<Layout />}>
              <Route index element={<Dashboard />} />
              <Route
                path={LearnAppRoutes.routes.catalog.route}
                element={
                  <RequireCatalogEnabled>
                    <Catalog />
                  </RequireCatalogEnabled>
                }
              />
              <Route path={LearnAppRoutes.routes.assessments.route} element={<Assessments />} />
              <Route
                path={LearnAppRoutes.routes.lesson.route}
                element={
                  <SingleLessonGuard>
                    <LessonPage />
                  </SingleLessonGuard>
                }
              />
              <Route
                path={LearnAppRoutes.routes.exercise.route}
                element={
                  <SingleLessonGuard>
                    <ExercisePage />
                  </SingleLessonGuard>
                }
              />
              <Route path={LearnAppRoutes.routes.level.route} element={<LevelPage />} />
              <Route path={LearnAppRoutes.routes.quests.route} element={<QuestsPage />} />
              <Route path={LearnAppRoutes.routes.path.route} element={<Path />} />
              <Route
                path={LearnAppRoutes.routes.quest.route}
                element={<Path pathType={"Quest"} />}
              />
              <Route
                path={LearnAppRoutes.routes.tournaments.routes.details.route}
                element={<TournamentPage />}
              />
              <Route
                path={LearnAppRoutes.routes.tournaments.routes.content.route}
                element={<TournamentContentPage />}
              />
              <Route
                path={LearnAppRoutes.routes.personalizedLessonAssignment.route}
                element={<PersonalizedLessonAssignmentPage />}
              />
              <Route path={LearnAppRoutes.routes.feedback.route} element={<FeedbackPage />} />
              <Route path={LearnAppRoutes.routes.leaderboard.route} element={<LeaderboardPage />} />
              <Route path={LearnAppRoutes.routes.aspenChat.route} element={<AspenChatPage />} />
              <Route
                path={LearnAppRoutes.routes.assessmentRedirect.route}
                element={<AssessmentRedirect />}
              />
              {/* Redirect legacy lesson routes */}
              <Route
                path={LearnAppRoutes.routes.legacyBeltRedirect.route}
                element={<LegacyLessonRedirect />}
              />
              <Route
                path={LearnAppRoutes.routes.legacyLessonRedirect.route}
                element={<LegacyLessonRedirect />}
              />
              {/* Redirects to make things work while we are transitioning to fully Atlas */}
              {/* Cleanup for the following routes are documented in SHRPA-3105 */}
              {/* Redirect "/journey" => "/" to dashboard */}
              <Route
                path={Redirects.journey}
                element={<Navigate to={LearnAppRoutes.route} replace />}
              />

              {/* Redirect "/account/assignments" to dashboard */}
              <Route
                path={Redirects.assignments}
                element={<Navigate to={LearnAppRoutes.route} replace />}
              />

              {/* Redirect "/tournaments/finished" => "/" to dashboard */}
              <Route
                path={Redirects.tournamentsFinished}
                element={<Navigate to={LearnAppRoutes.route} replace />}
              />

              {/* External redirect to User Profile Achievements */}
              <Route
                path={Redirects.profileAchievements}
                element={<ProfileAchievementRedirect />}
              />

              {/* Redirect /user/sign_in to dashboard for logged-in users */}
              <Route
                path={Redirects.signIn}
                element={<Navigate to={LearnAppRoutes.route} replace />}
              />

              {/* Redirect /modules/catalog to /catalog */}
              <Route
                path={Redirects.modulesCatalog}
                element={<Navigate to={LearnAppRoutes.routes.catalog.route} replace />}
              />

              {/** SCORM Route -> Routes to LessonPage **/}
              <Route
                path={LearnAppRoutes.routes.scorm.route}
                element={
                  <SingleLessonGuard>
                    <LessonPage />
                  </SingleLessonGuard>
                }
              />
              <Route
                path="*"
                element={
                  <ErrorPage
                    redirectRoute={LearnAppRoutes.route}
                    buttonText="Go to Dashboard"
                    automaticRedirect={false}
                  />
                }
              />
            </Route>
            {/** Guest Access is outside of the Layout **/}
            <Route path={LearnAppRoutes.routes.guest.route} element={<GuestLessonAccess />} />
            {/** Test Connection is outside of the Layout so it's accessible without auth **/}
            <Route path={LearnAppRoutes.routes.testConnection.route} element={<TestConnection />} />
            {/* SCORM Redirect Route -> Routes to Login */}
            <Route
              path={LearnAppRoutes.routes.scorm_redirect.route}
              element={<ScormLoginRedirect />}
            />
          </Routes>
        </LearnContextProvider>
      </ApiCacheProvider>
    </BrowserRouter>
  );
}

export default App;
